/**
 * Inert tip synthesis (ADR-010 §12) — generated rigs ship without tip bones
 * (Toe_End, HeadTop_End), but tips are load-bearing for IK handles (the
 * ballet-star lesson), reorientation (primaryChild ladder), and bone length.
 * Industry precedent: Blender/Maya/MotionBuilder auto-create leaf bones.
 *
 * Placement is GEOMETRY-FIRST: the furthest skinned-vertex extent along the
 * chain direction (we have the mesh — better than v2's fixed percentage);
 * the v2 fraction rule survives as the declared fallback when skinning is
 * thin. Every synthesized joint is reported and carries 'synthesized'
 * provenance — loud and correctable (§11), never a silent guess.
 * Depends on: three, @harryhausen/kernel. Depended on by: app/import.
 */
import { Matrix4, Object3D, SkinnedMesh, Vector3 } from 'three';
import {
  parseRigDescriptor,
  quatInvertUnit,
  quatRotateVec3,
  vec3Add,
  vec3Length,
  vec3Normalize,
  vec3Scale,
  vec3Sub,
  type MappingEntry,
  type MapRigResult,
  type Ontology,
  type RigDescriptor,
  type Vec3Data,
} from '@harryhausen/kernel';

/** v2's leaf-bone rule (ballet-star compendium: 10–30% of parent length) — data, not code. */
export const FALLBACK_TIP_FRACTION = 0.25;
const MIN_SKIN_WEIGHT = 0.3;

export interface TipGap {
  /** The tip ontology ID to synthesize (e.g. 'LeftToe_End'). */
  readonly tipOntologyId: string;
  /** The rig bone mapped to the tip's ontology parent (e.g. 'L_ToeBase'). */
  readonly parentRigBone: string;
}

export interface SynthesizedTipEntry {
  readonly name: string;
  readonly parentRigBone: string;
  readonly method: 'skinned-extent' | 'parent-fraction';
  /** Distance from the parent joint to the synthesized tip, rig units. */
  readonly extent: number;
}

export interface SynthesizeTipsResult {
  /** The descriptor with tips added as REAL bones (re-gated by the kernel parser). */
  readonly rig: RigDescriptor;
  /** Mapping entries for the new bones — provenance 'synthesized' (ADR-010 §12). */
  readonly mappingAdditions: readonly MappingEntry[];
  /** One entry per fabricated joint — the ImportReport's loud record. */
  readonly synthesized: readonly SynthesizedTipEntry[];
}

/**
 * Resolve which profile gaps are synthesizable tips: the gap is a tip bone,
 * it has NO real candidate (a withheld or conflict-demoted candidate means
 * something suspicious exists — fabricating a replacement would bury it and
 * unblock retargeting past an unreviewed contradiction — Codex, PR #15),
 * AND its ontology parent is mapped (a tip needs a parent joint to extend).
 * Everything else stays needs-review — synthesis never invents mid-chain
 * bones and never papers over evidence.
 */
export function synthesizableTipGaps(mapResult: MapRigResult, ontology: Ontology): TipGap[] {
  const mappedByOntologyId = new Map(mapResult.mapping.map((m) => [m.ontologyId, m.rigBone]));
  const gaps: TipGap[] = [];
  for (const review of mapResult.needsReview) {
    if (review.reason !== 'no-candidate') continue;
    const bone = ontology.byId.get(review.ontologyId);
    if (!bone?.tip || bone.parent === null) continue;
    const parentRigBone = mappedByOntologyId.get(bone.parent);
    if (parentRigBone !== undefined) gaps.push({ tipOntologyId: review.ontologyId, parentRigBone });
  }
  return gaps;
}

interface SkinnedVertexSource {
  readonly mesh: SkinnedMesh;
  readonly boneIndex: number;
}

function skinSourcesFor(scene: Object3D, boneName: string): SkinnedVertexSource[] {
  const sources: SkinnedVertexSource[] = [];
  scene.traverse((node) => {
    if (!(node instanceof SkinnedMesh) || !node.skeleton) return;
    const boneIndex = node.skeleton.bones.findIndex((b) => b.name === boneName);
    if (boneIndex >= 0) sources.push({ mesh: node, boneIndex });
  });
  return sources;
}

/** Furthest extent of vertices skinned to `boneName`, projected on `direction` from `origin`. */
function skinnedExtent(scene: Object3D, boneName: string, origin: Vec3Data, direction: Vec3Data): number | null {
  let extent: number | null = null;
  for (const { mesh, boneIndex } of skinSourcesFor(scene, boneName)) {
    const position = mesh.geometry.getAttribute('position');
    const skinIndex = mesh.geometry.getAttribute('skinIndex');
    const skinWeight = mesh.geometry.getAttribute('skinWeight');
    if (!position || !skinIndex || !skinWeight) continue;
    // At bind pose every joint contributes identity, so a vertex's bind-space
    // position is bindMatrix · geometry position — the same space as the
    // rig's IBM-derived worldBind (ADR-003 §2b).
    const bind = new Matrix4().copy(mesh.bindMatrix);
    const v = new Vector3();
    for (let i = 0; i < position.count; i += 1) {
      let weighted = false;
      for (let k = 0; k < 4; k += 1) {
        if (skinIndex.getComponent(i, k) === boneIndex && skinWeight.getComponent(i, k) >= MIN_SKIN_WEIGHT) {
          weighted = true;
          break;
        }
      }
      if (!weighted) continue;
      v.set(position.getX(i), position.getY(i), position.getZ(i)).applyMatrix4(bind);
      const t =
        (v.x - origin.x) * direction.x + (v.y - origin.y) * direction.y + (v.z - origin.z) * direction.z;
      if (extent === null || t > extent) extent = t;
    }
  }
  return extent;
}

/**
 * Fabricate the missing tips as real rig bones. The chain direction is
 * grandparent→parent continued (the only direction evidence a childless
 * terminal joint has); the tip's frame inherits the parent's rotation.
 */
export function synthesizeTips(
  scene: Object3D,
  rig: RigDescriptor,
  gaps: readonly TipGap[],
): SynthesizeTipsResult {
  const boneByName = new Map(rig.bones.map((b) => [b.name, b]));
  const newBones: RigDescriptor['bones'][number][] = [];
  const mappingAdditions: MappingEntry[] = [];
  const synthesized: SynthesizedTipEntry[] = [];

  for (const gap of gaps) {
    const parent = boneByName.get(gap.parentRigBone);
    if (!parent) continue; // caller passed a bone the rig doesn't have — skip, gap stays in review
    const grandparent = parent.parent !== null ? boneByName.get(parent.parent) : undefined;
    const parentPos = parent.worldBind.position;
    const chain =
      grandparent !== undefined ? vec3Sub(parentPos, grandparent.worldBind.position) : { x: 0, y: 1, z: 0 };
    const chainLength = vec3Length(chain);
    if (chainLength === 0) continue;
    const direction = vec3Normalize(chain);

    const measured = skinnedExtent(scene, gap.parentRigBone, parentPos, direction);
    const fallback = chainLength * FALLBACK_TIP_FRACTION;
    const useMeasured = measured !== null && measured > fallback * 0.25;
    const extent = useMeasured ? (measured as number) : fallback;

    const name = `${gap.parentRigBone}_${gap.tipOntologyId}_synth`;
    const worldPos = vec3Add(parentPos, vec3Scale(direction, extent));
    // Local frame: inherit the parent's rotation; position in the parent's frame.
    const offsetWorld = vec3Sub(worldPos, parentPos);
    const offsetLocal = quatRotateVec3(quatInvertUnit(parent.worldBind.rotation), offsetWorld);
    const s = parent.worldBind.scale;
    const localPos = { x: offsetLocal.x / s.x, y: offsetLocal.y / s.y, z: offsetLocal.z / s.z };
    const identity = { x: 0, y: 0, z: 0, w: 1 };
    newBones.push({
      name,
      parent: gap.parentRigBone,
      localBind: { position: localPos, rotation: identity, scale: { x: 1, y: 1, z: 1 } },
      worldBind: { position: worldPos, rotation: parent.worldBind.rotation, scale: { x: 1, y: 1, z: 1 } },
    });
    mappingAdditions.push({
      rigBone: name,
      ontologyId: gap.tipOntologyId,
      confidence: useMeasured ? 0.9 : 0.7,
      provenance: 'synthesized',
    });
    synthesized.push({
      name,
      parentRigBone: gap.parentRigBone,
      method: useMeasured ? 'skinned-extent' : 'parent-fraction',
      extent,
    });
  }

  // The kernel validator is the gate — synthesized bones are REAL bones.
  const withTips = parseRigDescriptor({ ...rig, bones: [...rig.bones, ...newBones] });
  return { rig: withTips, mappingAdditions, synthesized };
}
